import React, { useState, useEffect } from 'react';
import api from '../../services/api';
import { toast } from 'sonner';
import { Button } from '../../components/ui/button';
import { TrendDown, TrendUp, Warning, Package, ArrowRight, Clock, ShoppingCart } from '@phosphor-icons/react';

const SmartInventoryPage = () => {
  const [predictions, setPredictions] = useState([]);
  const [reorderList, setReorderList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState('predictions');

  useEffect(() => {
    Promise.all([
      api.getInventoryPredictions().catch(() => []),
      api.getReorderList().catch(() => []),
    ]).then(([p, r]) => {
      setPredictions(p);
      setReorderList(r);
      setLoading(false);
    });
  }, []);

  const urgencyColor = { critical: '#ef4444', high: '#f97316', medium: '#eab308' };
  const criticalItems = predictions.filter(p => p.days_until_empty !== null && p.days_until_empty <= 7);
  const warningItems = predictions.filter(p => p.days_until_empty !== null && p.days_until_empty > 7 && p.days_until_empty <= 30);

  if (loading) return <div className="flex items-center justify-center h-64"><div className="spinner" /></div>;

  return (
    <div data-testid="smart-inventory-page">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="heading-display">Smart Inventory</h1>
          <p className="text-slate-400 mt-1">Predictive stock tracking and automated reorder suggestions</p>
        </div>
        <div className="flex gap-2">
          <Button variant={view === 'predictions' ? 'default' : 'outline'} className={view === 'predictions' ? 'btn-primary' : 'btn-secondary'} size="sm" onClick={() => setView('predictions')} data-testid="view-predictions">
            <TrendDown size={14} className="mr-1" /> Predictions
          </Button>
          <Button variant={view === 'reorder' ? 'default' : 'outline'} className={view === 'reorder' ? 'btn-primary' : 'btn-secondary'} size="sm" onClick={() => setView('reorder')} data-testid="view-reorder">
            <ShoppingCart size={14} className="mr-1" /> Reorder List ({reorderList.length})
          </Button>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6" data-testid="inventory-stats">
        <div className="card-flat p-4 text-center">
          <p className="text-3xl font-bold" style={{ color: '#F1F5F9' }}>{predictions.length}</p>
          <p className="text-xs text-slate-400 mt-1">Total Items</p>
        </div>
        <div className="card-flat p-4 text-center border-red-800/40">
          <p className="text-3xl font-bold text-red-500">{criticalItems.length}</p>
          <p className="text-xs text-slate-400 mt-1">Critical (&lt;7 days)</p>
        </div>
        <div className="card-flat p-4 text-center border-amber-800/40">
          <p className="text-3xl font-bold text-amber-500">{warningItems.length}</p>
          <p className="text-xs text-slate-400 mt-1">Warning (&lt;30 days)</p>
        </div>
        <div className="card-flat p-4 text-center">
          <p className="text-3xl font-bold text-indigo-400">{reorderList.length}</p>
          <p className="text-xs text-slate-400 mt-1">Need Reorder</p>
        </div>
      </div>

      {view === 'predictions' ? (
        <div className="space-y-3" data-testid="predictions-list">
          {predictions.length === 0 ? (
            <div className="card-flat p-8 text-center text-slate-400">
              <Package size={40} className="mx-auto mb-2 opacity-40" />
              <p>No inventory items yet. Add items to start tracking consumption.</p>
            </div>
          ) : predictions.map(item => {
            const isUrgent = item.days_until_empty !== null && item.days_until_empty <= 7;
            const isWarning = item.days_until_empty !== null && item.days_until_empty <= 30;
            return (
              <div key={item.id} className={`card-flat p-4 transition-colors ${isUrgent ? 'border-red-800/40 bg-red-900/20/30' : isWarning ? 'border-amber-800/40 bg-amber-900/20/20' : ''}`} data-testid={`pred-${item.id}`}>
                <div className="flex items-center justify-between">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      {isUrgent && <Warning size={14} className="text-red-500" />}
                      <h3 className="font-semibold text-sm" style={{ color: '#F1F5F9' }}>{item.name}</h3>
                      <span className="text-[10px] px-1.5 py-0.5 bg-slate-100 rounded text-slate-300">{item.category}</span>
                    </div>
                    <div className="flex items-center gap-4 text-xs text-slate-400 mt-1">
                      <span>Stock: <strong>{item.quantity} {item.unit}</strong></span>
                      <span>Usage rate: <strong>{item.daily_consumption_rate}/day</strong></span>
                      <span>Used {item.usage_count}x</span>
                    </div>
                  </div>
                  <div className="text-right flex-shrink-0 ml-4">
                    {item.days_until_empty !== null ? (
                      <>
                        <p className={`text-lg font-bold ${isUrgent ? 'text-red-500' : isWarning ? 'text-amber-500' : 'text-indigo-400'}`}>
                          {Math.round(item.days_until_empty)}d
                        </p>
                        <p className="text-[10px] text-slate-400">until empty</p>
                        {item.predicted_empty_date && (
                          <p className="text-[10px] text-slate-400 flex items-center gap-1"><Clock size={10} /> {new Date(item.predicted_empty_date).toLocaleDateString()}</p>
                        )}
                      </>
                    ) : (
                      <p className="text-xs text-slate-400">No usage data</p>
                    )}
                  </div>
                </div>
                {/* Usage bar */}
                {item.minimum_quantity && (
                  <div className="mt-2">
                    <div className="w-full h-1.5 bg-slate-100 rounded-full overflow-hidden">
                      <div className="h-full rounded-full transition-all" style={{
                        width: `${Math.min(100, (item.quantity / (item.minimum_quantity * 3)) * 100)}%`,
                        backgroundColor: isUrgent ? '#ef4444' : isWarning ? '#eab308' : '#0d9488'
                      }} />
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      ) : (
        <div className="space-y-3" data-testid="reorder-list">
          {reorderList.length === 0 ? (
            <div className="card-flat p-8 text-center text-slate-400">
              <ShoppingCart size={40} className="mx-auto mb-2 opacity-40" />
              <p>All stock levels are adequate. No reorders needed.</p>
            </div>
          ) : reorderList.map((item, i) => (
            <div key={i} className="card-flat p-4 border-l-4" style={{ borderLeftColor: urgencyColor[item.urgency] || '#94a3b8' }} data-testid={`reorder-${i}`}>
              <div className="flex items-center justify-between">
                <div>
                  <div className="flex items-center gap-2">
                    <h3 className="font-semibold text-sm" style={{ color: '#F1F5F9' }}>{item.name}</h3>
                    <span className="text-[10px] px-1.5 py-0.5 rounded-full capitalize font-medium" style={{ backgroundColor: `${urgencyColor[item.urgency]}20`, color: urgencyColor[item.urgency] }}>{item.urgency}</span>
                  </div>
                  <p className="text-xs text-slate-400 mt-1">
                    Current: {item.current_quantity} {item.unit} | Min: {item.minimum_quantity} {item.unit}
                  </p>
                </div>
                <div className="text-right">
                  <p className="text-sm font-bold" style={{ color: '#F1F5F9' }}>Order: {item.suggested_reorder} {item.unit}</p>
                  <p className="text-[10px] text-slate-400">Suggested quantity</p>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default SmartInventoryPage;
